import { z } from "zod";
import type {
  AttendanceSetupStatus,
  AttendanceSetupStepKey,
} from "@/features/attendance/schemas/attendance-setup-status.schema";

/// URL search params for `/settings/attendance`.
///
/// `tab` is here rather than in `useState` (S3) so a link to the Shifts tab is
/// a link somebody can send. `calendarId` moved here from
/// `hr-settings.route.search.ts` when FND-015b collapsed the two calendar
/// editors onto this page: present, the Calendars tab renders one calendar's
/// detail instead of the card grid.
export const attendanceSettingsRouteSearchSchema = z
  .object({
    /**
     * `.catch(undefined)` so an UNKNOWN tab degrades to the default instead of
     * throwing a SearchParamError at the router.
     *
     * `rules` was a tab until the workspace rule moved to the foot of Shifts,
     * and a bookmark or a shared link still carrying it would otherwise render
     * a router error page rather than the screen it was pointing at. The set a
     * URL may name shrinks over time; the URLs people saved do not.
     */
    tab: z.enum(["calendars", "shifts", "assignments"]).optional().catch(undefined),
    calendarId: z.string().optional(),
    /// Present, the Shifts tab renders one shift's detail (its settings and its
    /// people) instead of the card list, the same grammar `calendarId` uses.
    shiftId: z.string().optional(),
    assignmentQuery: z.string().trim().max(100).optional(),
    assignmentState: z.enum(["current", "ended", "all"]).optional(),
    assignmentPage: z.number().int().positive().optional(),
  })
  .strict();

export type AttendanceSettingsRouteSearch = z.infer<typeof attendanceSettingsRouteSearchSchema>;

/// The tabs that still exist. `assignments` is accepted above for old links
/// only: putting people on a shift was a tab whose first control re-picked the
/// shift the Shifts tab had just listed, so it moved onto the shift itself.
export const ATTENDANCE_SETTINGS_TABS = ["calendars", "shifts"] as const;

export type AttendanceSettingsTab = (typeof ATTENDANCE_SETTINGS_TABS)[number];

/// Which tab fixes which setup step.
///
/// Two of the three map to themselves. Putting people on a shift is the
/// exception: an assignment belongs to a shift, so "who is on nothing" is
/// answered inside the Shifts tab. The readiness model keeps it as a separate
/// STEP because it fails separately, and this map is where that meets the tabs.
const TAB_FOR_STEP: Record<AttendanceSetupStepKey, AttendanceSettingsTab> = {
  calendars: "calendars",
  shifts: "shifts",
  assignments: "shifts",
};

export function tabForAttendanceSetupStep(step: AttendanceSetupStepKey): AttendanceSettingsTab {
  return TAB_FOR_STEP[step];
}

/// Which tab the URL means.
///
/// A `calendarId` names one calendar and a `shiftId` names one shift, and each
/// exists on exactly one tab, so a link carrying one lands there whatever `tab`
/// says. Without this, `?calendarId=x&tab=shifts` (a hand-edited or stale URL)
/// would open Shifts with a dead param and the calendar the link was about
/// would be nowhere.
export function resolveAttendanceSettingsTab(
  search: AttendanceSettingsRouteSearch,
  fallback: AttendanceSetupStepKey = "calendars",
): AttendanceSettingsTab {
  if (search.calendarId) return "calendars";
  if (search.shiftId) return "shifts";
  // A retired value lands on the tab that absorbed it rather than on the
  // default: somebody following an old link to the assignments tab wanted the
  // people on a shift, and dropping them on Work calendars would look like the
  // page had lost them.
  return tabForAttendanceSetupStep(search.tab ?? fallback);
}

/// Which step to open on: the first one that is not ready, because that is what the admin came to
/// fix. It used to be Work calendars every time, the tab revisited least (calendars are a yearly
/// job, people join shifts whenever somebody is hired). With nothing to fix it opens the
/// high-churn step; with no status yet, calendars. `optional` is never in the way.
export function initialAttendanceSetupStep(
  status: AttendanceSetupStatus | null,
): AttendanceSetupStepKey {
  if (!status) return "calendars";
  const blocker = status.steps.find((step) => step.state !== "ready" && step.state !== "optional");
  return blocker?.key ?? "assignments";
}
